import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CustomerService from '@/services/CustomerService';
import LeadsService from '@/services/LeadsService';
import LeadChannelsSelect from '@/components/LeadChannelsSelect';
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next';

export default function CustomerAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    email: '',
    tel: '',
    address: '',
    source: '',
    dob: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [leadPrefillError, setLeadPrefillError] = useState(null);
  const [leadCommunications, setLeadCommunications] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    let isMounted = true;
    if (!leadId) {
      setLeadCommunications([]);
      setLeadPrefillError(null);
      setPrefillLoading(false);
      return undefined;
    }

    setPrefillLoading(true);
    setLeadPrefillError(null);

    (async () => {
      try {
        const { data, error } = await LeadsService.getLeadById(leadId);
        if (!isMounted) return;
        if (error) throw new Error(error);
        if (!data) throw new Error(t('customers.form.messages.leadNotFound'));

        setFormData(prev => ({
          ...prev,
          title: data?.title ?? prev.title,
          name: data?.customerName ?? prev.name,
          email: data?.customerEmail ?? prev.email,
          tel: data?.customerTel ?? prev.tel,
          address: data?.customerAddress ?? data?.address ?? prev.address,
          source: data?.source ?? prev.source,
          dob: data?.dob?.slice?.(0, 10) ?? data?.dateOfBirth?.slice?.(0, 10) ?? prev.dob,
        }));

        const communications = Array.isArray(data?.communications) ? data.communications : [];
        setLeadCommunications(communications);
      } catch (err) {
        console.error('Failed to load lead data for customer form:', err);
        if (isMounted) {
          setLeadPrefillError(err.message || t('customers.form.messages.leadLoadFailed'));
          setLeadCommunications([]);
        }
      } finally {
        if (isMounted) {
          setPrefillLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [leadId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = { ...formData };

      if (leadId) {
        payload.leadId = leadId;
        payload.communications = leadCommunications;
      }

      const { success, error } = await CustomerService.createCustomer(payload);
      
      if (success) {
        navigate('/system/customers', { 
          state: { message: t('customers.form.messages.createdSuccess', { name: formData.name }) }
        });
      } else {
        setError(error || t('customers.form.messages.createFailed'));
      }
    } catch (err) {
      console.error('Unexpected error during customer creation:', err);
      setError(t('customers.form.messages.createUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="divide-y divide-gray-200 bg-white shadow sm:rounded-lg">
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base/7 font-semibold text-gray-900">{t('customers.form.titleAdd')}</h2>
            <p className="mt-1 text-sm/6 text-gray-500">{t('customers.form.subtitleAdd')}</p>
          </div>

          <form className="md:col-span-2" onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">

              <div className="sm:col-span-1">
                <label htmlFor="title" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.title')}
                </label>
                <div className="mt-2 grid grid-cols-1">
                  <select
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  >
                    <option value="">{t('customers.form.placeholders.select')}</option>
                    <option value="Mr">{t('customers.form.titleOptions.mr')}</option>
                    <option value="Mrs">{t('customers.form.titleOptions.mrs')}</option>
                    <option value="Ms">{t('customers.form.titleOptions.ms')}</option>
                  </select>
                  <ChevronDownIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-400 sm:size-4"
                  />
                </div>
              </div>

              <div className="sm:col-span-5">
                <label htmlFor="name" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.fullName')}
                </label>
                <div className="mt-2">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder={t('customers.form.placeholders.name')}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.emailAddress')}
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('customers.form.placeholders.email')}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="dob" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.dateOfBirth')}
                </label>
                <div className="mt-2">
                  <input
                    id="dob"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="tel" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.phone')}
                </label>
                <div className="mt-2">
                  <input
                    id="tel"
                    name="tel"
                    type="tel"
                    placeholder={t('customers.form.placeholders.phone')}
                    autoComplete="tel"
                    value={formData.tel}
                    onChange={handleInputChange}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="address" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.address')}
                </label>
                <div className="mt-2">
                  <textarea
                    id="address"
                    name="address"
                    placeholder={t('customers.form.placeholders.address')}
                    rows={3}
                    value={formData.address}
                    onChange={handleInputChange}
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="source" className="block text-sm/6 font-medium text-gray-900">
                  {t('customers.form.fields.source')}
                </label>
                <div className="mt-2">
                  <LeadChannelsSelect
                    id="source"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    placeholder={t('customers.form.placeholders.source')}
                  />
                </div>
              </div>

            </div>

            <div className="mt-8 flex gap-3">
              <button
                type="submit"
                disabled={loading || prefillLoading}
                className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors duration-200 ${
                  loading || prefillLoading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600'
                }`}
              >
                {loading ? t('customers.form.buttons.creating') : prefillLoading ? t('customers.form.buttons.loadingLead') : t('customers.form.buttons.create')}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/system/customers')}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50"
              >
                {t('customers.form.buttons.cancel')}
              </button>
            </div>

            {leadId && (
              <div className="mt-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                <span className="font-medium">{t('customers.form.leadInfo.label')}</span> {leadId}
                {prefillLoading && <span className="ml-2 text-xs text-indigo-500">{t('customers.form.leadInfo.loading')}</span>}
                {!prefillLoading && leadCommunications.length > 0 && (
                  <span className="ml-2 text-xs text-indigo-500">{t('customers.form.leadInfo.communicationsImported', { count: leadCommunications.length })}</span>
                )}
              </div>
            )}

            {leadPrefillError && (
              <div className="mt-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                {leadPrefillError}
              </div>
            )}
          </form>
        </div>

      </div>
    </>
  )
}
